/**
 * Per-environment scan state helpers.
 *
 * See docs/spec-initial-load-env-scan-state.md.
 * The storage layer (OPFS) owns the `environments` table; this file collects
 * constants and pure helpers that the rest of the app uses to reason about
 * scan freshness and render ages.
 */

/** How long a completed scan stays "fresh" before auto-refresh kicks in. */
export const ENV_SCAN_STALE_MS = 60 * 60 * 1000;

/**
 * Compact human-friendly age formatter for "refreshed Xh Ym ago" labels.
 * Negatives (clock skew) clamp to "just now". Examples:
 *   30_000      -> "just now"
 *   90_000      -> "1m"
 *   3_605_000   -> "1h"
 *   5_460_000   -> "1h 31m"
 *   90_000_000  -> "1d 1h"
 */
export function formatAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 60_000) return 'just now';
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

/**
 * "X ago" phrase that reads correctly for sub-minute ages — `formatAge` returns
 * "just now" which doesn't pair with " ago" ("just now ago" is wrong).
 */
export function formatAgeAgo(ms: number): string {
  if (!Number.isFinite(ms) || ms < 60_000) return 'just now';
  return `${formatAge(ms)} ago`;
}
